import React, { useState, useEffect, useMemo, ChangeEvent } from 'react';
import { useMutation } from '@apollo/client';

import {
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
} from '@mui/material';

import ErrorView from '../../../components/common/ErrorView';

import { FragmentType, useFragment } from '../../../__generated__';
import { UPDATE_AGENT, ADD_AGENT } from '../../../graphql/agent';
import { ClientAgent_ItemFragment } from './ClientAgentItem';
import {
  AgentRoleKeys,
  addAgentRolesWithTitles,
  getAgentRolesWithTitles,
  isSatelliteRole,
  isSuperRole,
  validatePhone,
} from 'gmd-utilities-package';
import FormDialogContainer, {
  DialogBody,
} from '../../../components/common/FormDialogContainer';

type EditClientAgentContainerProps = {
  open: boolean;
  tenantId: string;
  currency: string;
  agent?: FragmentType<typeof ClientAgent_ItemFragment>;
  onClose: (itemChanged?: boolean) => void;
};

const defaultRoles = getAgentRolesWithTitles([
  AgentRoleKeys.super,
  AgentRoleKeys.satellite,
]);

const EditClientAgentContainer = (props: EditClientAgentContainerProps) => {
  const { open, tenantId, currency, onClose } = props;
  const agent = useFragment(ClientAgent_ItemFragment, props.agent) || null;
  const [updateAgent, { loading: updateLoading, error: updateError }] =
    useMutation(UPDATE_AGENT);
  const [addAgent, { loading: addLoading, error: addError }] =
    useMutation(ADD_AGENT);
  const [name, setName] = useState<string>(agent?.name || '');
  const [email, setEmail] = useState<string>(agent?.email || '');
  const [phone, setPhone] = useState<string>(agent?.phone ? agent.phone : '');
  const [role, setRole] = useState<string>(agent?.role ? agent.role : '');
  const [formError, setFormError] = useState<string>('');
  const [agentRoles, setAgentRoles] = useState<{ [key: string]: string }>(
    defaultRoles
  );

  const loading = agent ? updateLoading : addLoading;
  const error = agent ? updateError : addError;

  useEffect(() => {
    setName(agent?.name || '');
    setEmail(agent?.email || '');
    setPhone(agent?.phone || '');
    setRole(agent?.role || '');
    if (agent && agent.role) {
      setAgentRoles(addAgentRolesWithTitles(defaultRoles, [agent.role]));
    }
  }, [agent, setAgentRoles]);

  useEffect(() => {
    const error = validatePhone(phone, currency);
    setFormError(error);
  }, [phone, currency, setFormError]);

  const canEditAgentRole = useMemo(() => {
    const isSuper = isSuperRole(agent?.role || '');
    const isSatellite = isSatelliteRole(agent?.role || '');
    return isSuper || isSatellite;
  }, [agent]);

  const handleRoleChange = (event: SelectChangeEvent<string>) => {
    setRole(event.target.value);
  };

  const handleStringChange = (
    element: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    updateCallBack: (value: string) => void
  ) => {
    const value = element.target.value;
    updateCallBack(value);
  };

  const allowSubmit = () => {
    const result = name && email && phone && role && !formError && !loading;
    return Boolean(result);
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFormError('');
    if (allowSubmit()) {
      if (agent) {
        const result = await updateAgent({
          variables: {
            tenantId,
            id: agent.id,
            name,
            email,
            phone,
            role,
          },
        });
        if (result?.data?.updateAgent?.success) {
          onClose(true);
        } else {
          setFormError(result?.data?.updateAgent?.message);
        }
      } else {
        const result = await addAgent({
          variables: {
            tenantId,
            name,
            email,
            phone,
            role,
            limit: 0,
          },
        });
        if (result?.data?.addAgent?.success) {
          onClose(true);
        } else {
          setFormError(result?.data?.addAgent?.message);
        }
      }
    } else {
      setFormError(
        'There was an issue with the form submission. Please contact system admin.'
      );
    }
  };

  const isEditMode = agent && agent.id;
  const errorMessage = formError || error?.message;

  return (
    <>
      <FormDialogContainer>
        <DialogBody
          open={open}
          loading={loading}
          disabled={!allowSubmit()}
          title={isEditMode ? 'Edit Agent' : 'New Agent'}
          onClose={onClose}
          onSubmit={handleSubmit}
        >
          <Grid container spacing={3} sx={{ pt: 1 }}>
            <Grid item xs={12}>
              <TextField
                autoFocus
                margin="dense"
                id="name"
                name="name"
                label="Name"
                value={name}
                onChange={(e) =>
                  handleStringChange(e, (value: string) => setName(value))
                }
                type="text"
                fullWidth
                required
                variant="standard"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                margin="dense"
                id="email"
                name="email"
                label="Email"
                value={email}
                onChange={(e) =>
                  handleStringChange(e, (value: string) => setEmail(value))
                }
                type="text"
                fullWidth
                required
                variant="standard"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                margin="dense"
                id="phone"
                name="phone"
                label="Phone"
                value={phone}
                onChange={(e) =>
                  handleStringChange(e, (value: string) => setPhone(value))
                }
                type="text"
                fullWidth
                required
                variant="standard"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl required fullWidth>
                <InputLabel id={'label-role'} style={{ left: -15 }}>
                  Role
                </InputLabel>
                <Select
                  variant="standard"
                  labelId={'label-role'}
                  name={'role'}
                  id={'role'}
                  label={'Role'}
                  value={role}
                  readOnly={canEditAgentRole}
                  onChange={handleRoleChange}
                >
                  {Object.keys(agentRoles).map((key) => (
                    <MenuItem value={key} key={key}>
                      {agentRoles[key]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          {errorMessage && <ErrorView message={errorMessage} />}
        </DialogBody>
      </FormDialogContainer>
    </>
  );
};

export default EditClientAgentContainer;
